import React from 'react';
import * as d3 from 'd3';

export default class DraggableAreaChart extends React.Component {

    div = null;
    svg = null;
    focus = null;
    drag = null;
    zoom = null;

    svgWidth = 1100;
    svgHeight = this.props.height ? this.props.height : 400;

    margin = { top: 10, right: 20, bottom: 30, left: 50 };
    width = this.svgWidth - this.margin.left - this.margin.right;
    height = this.svgHeight - this.margin.top - this.margin.bottom;

    x = null;
    y = null;

    xAxis = null;
    yAxis = null;

    area = null;
    line = null;

    overlayArea = null;
    overlayLine = null;

    start_x = null;
    start_y = null;
    start_screen_x = null;
    start_screen_y = null;

    isMouseOver = false;

    constructor(props) {
        super(props);

        this.handleChange = this.handleChange.bind(this);

        this.onDragStarted = this.onDragStarted.bind(this);
        this.onDragged = this.onDragged.bind(this);
        this.onDragEnded = this.onDragEnded.bind(this);

        this.onMouseOver = this.onMouseOver.bind(this);
        this.onMouseOut = this.onMouseOut.bind(this);

        this.onZoom = this.onZoom.bind(this)
    }

    handleChange(e) {
        const value = e.target.value;
        this.props.handleChange(e.target.name, value);
    }

    drawAreaChart() {

        const dataChart = this.props.dataChart;

        const maxX = this.props.max !== undefined && this.props.max.x !== undefined ? this.props.max.x : d3.max(dataChart, (d) => d.x);
        const maxY = this.props.max !== undefined && this.props.max.y !== undefined ? this.props.max.y : d3.max(dataChart, (d) => d.y);

        const minX = this.props.min !== undefined && this.props.min.x !== undefined ? this.props.min.x : d3.min(dataChart, (d) => d.x);
        const minY = this.props.min !== undefined && this.props.min.y !== undefined ? this.props.min.y : d3.min(dataChart, (d) => d.y);

        const yLabelOffset = 10;
        const xLabelOffset = 17;

        // -------------
        // Add X axis
        this.x.domain(this.props.isXDesc ? [maxX, minX] : [minX, maxX]);

        // Add Y axis
        this.y.domain(this.props.isYDesc ? [maxY, minY] : [minY, maxY]);

        // Fill the area

        if (this.props.sections) {
            let lastSection = dataChart[0].section;
            let points = [{ x: dataChart[0].x, y: dataChart[0].y }];
            for (let i = 1; i < dataChart.length; i++) {
                let p = dataChart[i];
                if (p.section && p.section !== lastSection) {
                    points.push({ x: points[points.length - 1].x, y: minY })
                    points.push({ x: points[0].x, y: minY })
                    this.svg.append("path")
                        .datum(points)
                        .attr("class", this.props.sections[lastSection])
                        .attr("stroke", "none")
                        .attr("d", this.area);

                    // add the line
                    this.svg.append("path")
                        .datum(points)
                        .attr("class", "line")
                        .attr("fill", "none")
                        .attr("stroke", "black")
                        .attr("stroke-width", 1)
                        .attr("d", this.line);
                    lastSection = p.section;
                    points = [{ x: dataChart[i - 1].x, y: dataChart[i - 1].y }];
                }
                points.push({ x: p.x, y: p.y });
            }
            this.svg.append("path")
                .datum(points)
                .attr("class", this.props.sections[lastSection])
                .attr("stroke", "none")
                .attr("d", this.area);

            // add the line
            this.svg.append("path")
                .datum(points)
                .attr("class", "line")
                .attr("fill", "none")
                .attr("stroke", "black")
                .attr("stroke-width", 1)
                .attr("d", this.line);
        }
        else {
            this.svg.append("path")
                .datum(dataChart)
                .attr("class", "area-dark")
                .attr("stroke", "none")
                .attr("d", this.area);
        }

        if (this.props.type === "secondary") {
            this.svg.append("path")
                .datum(dataChart)
                .attr("fill", "white")
                .attr("stroke", "blue")
                .attr("stroke-width", "3px")
                .attr("d", this.area);
        }

        // add gradients
        this.svg.append("linearGradient")
            .attr("id", "area-gradient-dark")
            .attr("gradientUnits", "userSpaceOnUse")
            .attr("x1", 0).attr("y1", this.y(d3.min(dataChart, (d) => d.y)))
            .attr("x2", 0).attr("y2", this.y(d3.max(dataChart, (d) => d.y)))
            .selectAll("stop")
            .data([
                { offset: "0%", color: "#fdcfa1" },
                { offset: "100%", color: "#E95B0D" }
            ])
            .enter().append("stop")
            .attr("offset", function (d) { return d.offset; })
            .attr("stop-color", function (d) { return d.color; });
        this.svg.append("linearGradient")
            .attr("id", "area-gradient-light")
            .attr("gradientUnits", "userSpaceOnUse")
            .attr("x1", 0).attr("y1", this.y(d3.min(dataChart, (d) => d.y)))
            .attr("x2", 0).attr("y2", this.y(d3.max(dataChart, (d) => d.y)))
            .selectAll("stop")
            .data([
                { offset: "0%", color: "#fdcfa1" },
                { offset: "100%", color: "#f79e6f" }
            ])
            .enter().append("stop")
            .attr("offset", function (d) { return d.offset; })
            .attr("stop-color", function (d) { return d.color; });

        // add the line
        this.svg.append("path")
            .datum(dataChart)
            .attr("class", "line")
            .attr("fill", "none")
            .attr("stroke", "black")
            .attr("stroke-width", 2)
            .attr("d", this.line);

        // Optional second curve on the same x/y scale (e.g. a sensor-detected level curve
        // overlaid on the same time axis as the main schedule curve)
        if (this.props.overlayDataChart && this.props.overlayDataChart.length > 0) {
            const overlayColor = this.props.overlayColor || "#1f6fb2";

            // Shade below the overlay line with a gradient that fades to fully transparent toward
            // the baseline, so the schedule's own area underneath stays readable through it.
            if (this.props.overlayAreaGradient) {
                const overlayTop = d3.max(this.props.overlayDataChart, (d) => +d.y || 0);

                this.svg.append("linearGradient")
                    .attr("id", "overlay-area-gradient")
                    .attr("gradientUnits", "userSpaceOnUse")
                    .attr("x1", 0).attr("y1", this.y(overlayTop))
                    .attr("x2", 0).attr("y2", this.y(minY))
                    .selectAll("stop")
                    .data([
                        { offset: "0%", opacity: 0.35 },
                        { offset: "100%", opacity: 0 }
                    ])
                    .enter().append("stop")
                    .attr("offset", (d) => d.offset)
                    .attr("stop-color", overlayColor)
                    .attr("stop-opacity", (d) => d.opacity);

                this.svg.append("path")
                    .datum(this.props.overlayDataChart)
                    .attr("fill", "url(#overlay-area-gradient)")
                    .attr("stroke", "none")
                    .attr("d", this.overlayArea);
            }

            const overlayPath = this.svg.append("path")
                .datum(this.props.overlayDataChart)
                .attr("fill", "none")
                .attr("stroke", overlayColor)
                .attr("stroke-width", this.props.overlayStrokeWidth || 2)
                .attr("d", this.overlayLine);

            // An overlay that reads as a full series of its own gets a solid stroke and no point
            // markers. Opt into dashes and dots to mark it as subordinate to the main curve instead.
            if (this.props.overlayDashed) {
                overlayPath.attr("stroke-dasharray", "6,3");

                this.svg.selectAll(".overlay-point")
                    .data(this.props.overlayDataChart)
                    .enter()
                    .append("circle")
                    .attr("class", "overlay-point")
                    .attr("r", 3)
                    .attr("cx", (d) => this.x(d.x))
                    .attr("cy", (d) => this.y(d.y))
                    .attr("fill", overlayColor);
            }
        }

        // Add the Circles
        // this.svg.selectAll("circle")
        //     .data(dataChart)
        //     .enter()
        //     .append('circle')
        //     .attr('r', 5.0)
        //     .attr("cx", (d) => { return this.x(d.x) })
        //     .attr("cy", (d) => { return this.y(d.y) })
        //     .style('cursor', 'pointer')
        //     .style('fill', 'white')
        //     .attr("stroke", "black")
        //     .attr("stroke-width", 1)
        //     .on("mouseover", this.onMouseOver)
        //     .on("mouseout", this.onMouseOut)
        //     .call(this.drag);

        // Add the x axis
        this.svg.append("g")
            .attr('class', 'axis axis--x')
            .attr("transform", "translate(0," + this.height + ")")
            .call(this.xAxis);

        // Add the y axis
        this.svg.append("g")
            .attr('class', 'axis axis--y')
            .call(this.yAxis);

        if (this.props.yThresholds) {

            for (let i = 0; i < this.props.yThresholds.length; i++) {
                this.svg.append("line")
                    .attr("x1", this.x(minX))
                    .attr("y1", this.y(this.props.yThresholds[i].value))
                    .attr("x2", this.x(maxX))
                    .attr("y2", this.y(this.props.yThresholds[i].value))
                    .style("stroke-dasharray", "5,5")
                    .style("stroke", this.props.yThresholds[i].color);
            }
        }

        if (this.props.xThresholds) {

            for (let i = 0; i < this.props.xThresholds.length; i++) {
                this.svg.append("line")
                    .attr("x1", this.x((this.props.xThresholds[i].value)))
                    .attr("y1", this.y(minY))
                    .attr("x2", this.x((this.props.xThresholds[i].value)))
                    .attr("y2", this.y(maxY))
                    .style("stroke-dasharray", "5,5")
                    .style("stroke", this.props.xThresholds[i].color);
            }
        }

        if (this.props.yLabels) {
            for (let i = 0; i < this.props.yLabels.length; i++) {
                this.svg.append("line")
                    .attr("x1", this.x(this.props.rotateYAxis ? maxX : minX))
                    .attr("y1", this.y(this.props.yLabels[i].y))
                    .attr("x2", this.x(this.props.rotateYAxis ? maxX : minX) - yLabelOffset / 2)
                    .attr("y2", this.y(this.props.yLabels[i].y))
                    .style("stroke", "rgb(0,0,0)");
                this.svg.append("text")
                    .attr("x", this.x(this.props.rotateYAxis ? maxX : minX) - yLabelOffset)
                    .attr("y", this.y(this.props.yLabels[i].y))
                    .text(this.props.yLabels[i].label)
                    .attr("text-anchor", "end")
                    .attr("alignment-baseline", "middle");
            }
        }

        if (this.props.xLabels) {
            for (let i = 0; i < this.props.xLabels.labels.length; i++) {
                const el = this.props.xLabels.labels[i];
                this.svg.append("text")
                    .attr("x", this.x(el.x))
                    .attr("y", this.y(minY) + xLabelOffset)
                    .text(el.label)
                    .attr("text-anchor", "middle")
                    .attr("alignment-baseline", "top")
                    .attr("font-size", "0.7rem");
            }
            for (let i = 0; i < this.props.xLabels.smallLabels.length; i++) {
                const el = this.props.xLabels.smallLabels[i];
                this.svg.append("text")
                    .attr("x", this.x(el.x))
                    .attr("y", this.y(minY) + xLabelOffset / 1.75)
                    .text(el.label)
                    .attr("text-anchor", "middle")
                    .attr("alignment-baseline", "top")
                    .attr("font-size", "0.6rem");
            }
            for (let i = 0; i < this.props.xLabels.ticks.length; i++) {
                const el = this.props.xLabels.ticks[i];
                this.svg.append("line")
                    .attr("x1", this.x(el))
                    .attr("y1", this.y(minY))
                    .attr("x2", this.x(el))
                    .attr("y2", this.y(minY) + yLabelOffset / 1.75)
                    .style("stroke", "rgb(0,0,0)");

            }
        }
    }

    onMouseOver(d, i, array) {
        d3.select(array[i]).style("cursor", "move");

        this.div.transition()
            .duration(200)
            .style("opacity", .9);
        this.div.html(`${this.props.x.title}: ${d.x}</br>${this.props.y.title}: ${d.y}`)
            .style("left", (d3.event.pageX - 140) + "px")
            .style("top", (d3.event.pageY - 50) + "px");
    }

    onMouseOut(d, i, array) {
        this.div.transition()
            .duration(500)
            .style("opacity", 0);
    }

    onDragStarted(d, i, array) {
        d3.select(array[i]).raise().classed('active', true);

        this.start_x = +d3.event.x;
        this.start_y = +d3.event.y;
        this.start_screen_x = +d3.event.sourceEvent.screenX;
        this.start_screen_y = +d3.event.sourceEvent.screenY;
    }

    onDragged(d, i, array) {

        const changedX = d3.event.sourceEvent.screenX - this.start_screen_x;
        const changedY = this.start_screen_y - d3.event.sourceEvent.screenY;

        // let current_scale = 1;
        // if (array[i].getAttribute("transform") === null)
        // {
        //     current_scale = 1;
        // }
        // else {
        //     const current_scale_string = this.getAttribute("transform").split(' ')[1];
        //     current_scale = +current_scale_string.substring(6,current_scale_string.length-1);
        // }

        // d['x'] = Math.round(this.x.invert(this.x(this.start_x) + changedX) / current_scale);
        // d['y'] = Math.round(this.y.invert(this.y(this.start_y) - changedY) / current_scale);

        d['x'] = Math.round(this.x.invert(this.x(this.start_x) + changedX));
        d['y'] = Math.round(this.y.invert(this.y(this.start_y) - changedY));

        if (i > 0) {

            const prevx = Math.round(this.x.invert(d3.select(array[i - 1]).attr('cx')));
            const prevy = Math.round(this.y.invert(d3.select(array[i - 1]).attr('cy')));

            if (d.x < prevx) {
                d.x = prevx;
            }
            if (d.y <= prevy) {
                d.y = prevy + 1;
            }
        }

        if (i < (array.length - 1)) {

            const nextx = Math.round(this.x.invert(d3.select(array[i + 1]).attr('cx')));
            const nexty = Math.round(this.y.invert(d3.select(array[i + 1]).attr('cy')));

            if (d.x > nextx) {
                d.x = nextx;
            }
            if (d.y >= nexty) {
                d.y = nexty - 1;
            }
        }

        if (d.x < 0) {
            d.x = 0;
        } else if (d.x > 255) {
            d.x = 255;
        }

        if (d.y < 0) {
            d.y = 0;
        } else if (d.y > 100) {
            d.y = 100;
        }

        if (i === 0 && d.x > 0) {
            d.x = 0;
        }

        d3.select(array[i]).attr('cx', this.x(d.x)).attr('cy', this.y(d.y));

        this.svg.select('path.area').attr('d', this.area);
        this.svg.select('path.line').attr('d', this.line);
    }

    onDragEnded(d, i, array) {
        d3.select(array[i]).classed('active', false);

        this.start_x = null;
        this.start_y = null;

        this.start_screen_x = null;
        this.start_screen_y = null;

        this.props.handleChange(d, i);
    }

    onZoom(d) {
        this.svg.attr("transform", d3.event.transform);
    }

    initializeChart() {

        // get div for tooltip
        this.div = d3.select("body").append("div")
            .attr("class", "tooltip")
            .style("opacity", 0);

        // get svg
        this.svg = d3.select("#" + (this.props.id ? this.props.id : "area"));

        // get x scale
        if (this.props.scale && this.props.scale.x && this.props.scale.x.toUpperCase() === 'LOG') {
            this.x = d3.scaleSymlog().rangeRound([0, this.width]);
        } else {
            this.x = d3.scaleLinear().rangeRound([0, this.width]);
        }

        // get y scale
        if (this.props.scale && this.props.scale.y && this.props.scale.y.toUpperCase() === 'LOG') {
            this.y = d3.scaleSymlog().rangeRound([this.height, 0]);
        } else {
            this.y = d3.scaleLinear().rangeRound([this.height, 0]);
        }

        // get x axis
        this.xAxis = d3.axisBottom(this.x);
        if (this.props.tickFormat && this.props.tickFormat.x) {
            this.xAxis = this.xAxis.tickFormat(this.props.tickFormat.x);
        }
        if (this.props.tickValues && this.props.tickValues.x) {
            this.xAxis = this.xAxis.tickValues(this.props.tickValues.x);
        }

        // get y axis
        this.yAxis = d3.axisLeft(this.y);
        if (this.props.tickFormat && this.props.tickFormat.y) {
            this.yAxis = this.yAxis.tickFormat(this.props.tickFormat.y);
        }
        if (this.props.tickValues && this.props.tickValues.y) {
            this.yAxis = this.yAxis.tickValues(this.props.tickValues.y);
        }

        // text label for the x axis
        this.svg.append("text")
            .attr("transform", "translate(" + (this.width / 2) + " ," + (this.svgHeight) + ")")
            .style("text-anchor", "middle")
            .text(this.props.x.title);

        // text label for the y axis
        this.svg.append("text")
            .attr("transform", "")
            .attr("transform", "translate(" + (this.margin.right - 10) + "," + (this.svgHeight / 2) + ") rotate(-90)")
            .style("text-anchor", "middle")
            .text(this.props.y.title);

        // Area
        this.area = d3.area()
            .x((d) => { return this.x(d.x) })
            .y0(this.y(0))
            .y1((d) => { return this.y(d.y) });

        // Line
        this.line = d3.line()
            .x((d) => { return this.x(d.x) })
            .y((d) => { return this.y(d.y) });

        // Overlay generators. The main series encodes its fade ramps as explicit point pairs so it
        // must stay linearly interpolated; an overlay that instead carries one point per breakpoint
        // (a level held until the next breakpoint) wants step-after interpolation.
        const overlayCurve = this.props.overlayStep ? d3.curveStepAfter : d3.curveLinear;
        this.overlayArea = d3.area()
            .curve(overlayCurve)
            .x((d) => { return this.x(d.x) })
            .y0(this.y(0))
            .y1((d) => { return this.y(d.y) });
        this.overlayLine = d3.line()
            .curve(overlayCurve)
            .x((d) => { return this.x(d.x) })
            .y((d) => { return this.y(d.y) });

        // !! d3 v5-era code, NOT converted for v7 !!
        //
        // Neither behaviour is attached: `.call(this.drag)` is commented out above and
        // nothing in the repo calls `.call(this.zoom)`, so the handlers below never run.
        // That is why the v5 -> v7 bump was safe — no live code reads the removed
        // `d3.event`.
        //
        // Before re-enabling any of this, the handlers MUST be converted, or they will
        // throw. In v6+ a listener receives the event as its FIRST argument and the old
        // (d, i, array) signature is gone:
        //
        //   onDragStarted(d, i, array)      ->  onDragStarted(event, d)
        //   d3.event.x / .sourceEvent       ->  event.x / event.sourceEvent
        //   d3.event.transform  (onZoom)    ->  event.transform
        //   d3.event.pageX      (tooltips)  ->  event.pageX
        //   d3.select(array[i])             ->  d3.select(event.currentTarget)
        //
        // Left unconverted deliberately: dead code cannot be tested, and rewriting it
        // blind during a dependency bump would ship unverifiable behaviour.

        // Add drag
        this.drag = d3.drag()
        // .on('start', this.onDragStarted)
        // .on('drag', this.onDragged)
        // .on('end', this.onDragEnded);

        // Add zoom
        this.zoom = d3.zoom()
            .on("zoom", this.onZoom);

        // this.svg.append('rect')
        //     .attr('class', 'zoom')
        //     .attr('cursor', 'move')
        //     .attr('fill', 'none')
        //     .attr('pointer-events', 'all')
        //     .attr('width', this.width)
        //     .attr('height', this.height)
        //     .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')');

        // better translation
        this.svg = this.svg.append("g")
            .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");

    }

    componentDidUpdate() {
        const chart = document.getElementById(this.props.id ? this.props.id : "area");
        while (chart.hasChildNodes()) {
            chart.removeChild(chart.lastChild);
        }

        this.div.transition()
            .style("opacity", 0);

        this.initializeChart();

        if (!this.props.containsErrors) {
            this.drawAreaChart();
        } else {
            this.initializeChart();
        }
    }

    componentDidMount() {
        this.initializeChart();
        if (!this.props.containsErrors) {
            this.drawAreaChart();
        } else {
            this.initializeChart();
        }
    }

    render() {

        return (
            <svg
                id={this.props.id ? this.props.id : "area"}
                onChange={this.handleChange}
                width={"100%"}
                height={this.svgHeight + 20}
                className={this.props.disabled ? "center disabled" : "center"}>
            </svg>
        );
    }
}
